import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import ContactFormDialog from "@/components/ContactFormDialog";
import { 
  FolderSearch, 
  RefreshCw, 
  EyeOff, 
  HelpCircle,
  Upload,
  Search,
  Lightbulb,
  Users,
  MessageSquare,
  ClipboardList,
  Crown,
  ArrowRight,
  ChevronDown,
  ChevronUp
} from "lucide-react";

const CrosierHome: React.FC = () => {
  const [contactOpen, setContactOpen] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const challenges = [
    {
      icon: FolderSearch,
      title: "Fragmented Content",
      description: "Evangelization materials scattered across folders, drives, and staff members with no unified system."
    },
    {
      icon: RefreshCw,
      title: "Reinventing the Wheel",
      description: "Each diocese starts from scratch instead of learning from what's already working elsewhere."
    },
    {
      icon: EyeOff,
      title: "No Visibility",
      description: "Staff lack insight into what other dioceses are successfully doing to reach souls."
    },
    {
      icon: HelpCircle,
      title: "Repeated Questions",
      description: "Bishops and leaders ask the same evangelization questions with no shared resource base."
    }
  ];

  const phases = [
    {
      icon: Upload,
      phase: "Crawl",
      title: "Upload & Organize",
      description: "Upload and tag diocesan evangelization documents with a standardized taxonomy designed for Catholic ministry."
    },
    {
      icon: Search,
      phase: "Walk",
      title: "AI-Assisted Discovery",
      description: "Natural language search to find relevant resources. Identify best practices across dioceses instantly."
    },
    {
      icon: Lightbulb,
      phase: "Run",
      title: "Bishop-Directed Insights",
      description: "Voice interface for bishops. Personalized evangelization strategy summaries and cross-diocese pattern detection."
    }
  ];

  const personas = [
    {
      icon: Users,
      title: "Directors of Evangelization",
      description: "Organize and discover proven strategies. Stop reinventing the wheel."
    },
    {
      icon: MessageSquare,
      title: "Communication Directors",
      description: "Access templates and messaging that works. Learn from successful campaigns."
    },
    {
      icon: ClipboardList,
      title: "Pastoral Planning Offices",
      description: "Learn from what's working in other dioceses. Make data-informed decisions."
    },
    {
      icon: Crown,
      title: "Bishops",
      description: "Get insights on evangelization patterns and priorities across your diocese and beyond."
    }
  ];

  const faqs = [
    {
      question: "What is the Catholic Evangelization Repository?",
      answer: "A centralized platform for dioceses to upload, organize, and discover evangelization content with AI-assisted search. Think of it as a shared brain for Catholic evangelization efforts across the United States."
    },
    {
      question: "How does the pilot program work?",
      answer: "We're inviting 3-5 dioceses to test the platform in Q1 2026. Pilot participants provide feedback that shapes the final product and receive priority support and early access to new features."
    },
    {
      question: "What can I upload to the repository?",
      answer: "Evangelization plans, pastoral letters, preaching series, discipleship processes, parish renewal programs, and other evangelization materials. The platform accepts documents, presentations, and structured content."
    },
    {
      question: "Is my diocese's content private?",
      answer: "You control visibility settings. Content can be private to your diocese only, shared with specific partner dioceses, or made available to the broader network. You decide what to share and with whom."
    }
  ];

  const contactReasons = [
    { value: "Pilot Application", label: "Pilot Application" },
    { value: "Partnership Inquiry", label: "Partnership Inquiry" },
    { value: "Technical Questions", label: "Technical Questions" }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <p className="text-primary font-medium tracking-wide uppercase text-sm">
              AI tools to support the shepherds of the Church
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground leading-tight">
              Organize. Discover. Share.
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto">
              A unified platform for diocesan evangelization content
            </p>
            <p className="text-lg text-muted-foreground/80 max-w-3xl mx-auto">
              We're building a Catholic Evangelization Repository—a centralized, AI-searchable platform 
              where dioceses upload, organize, and discover evangelization resources.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button 
                size="lg" 
                onClick={() => setContactOpen(true)}
                className="text-lg px-8"
              >
                Apply for the Pilot
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-lg px-8"
              >
                Learn How It Works
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* The Challenge Section */}
      <section className="py-16 lg:py-24 bg-card/50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              The Challenge
            </h2>
            <p className="text-lg text-muted-foreground">
              Evangelization efforts across dioceses face common obstacles that limit their effectiveness.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {challenges.map((challenge, index) => (
              <Card key={index} className="border-border/50 bg-card">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-destructive/10">
                      <challenge.icon className="h-6 w-6 text-destructive" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        {challenge.title}
                      </h3>
                      <p className="text-muted-foreground">
                        {challenge.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* The Solution Section */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              The Solution
            </h2>
            <p className="text-lg text-muted-foreground">
              A platform designed specifically for the unique needs of Catholic diocesan evangelization.
            </p>
          </div>
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* Before */}
              <Card className="border-destructive/30 bg-destructive/5">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-destructive mb-4 flex items-center gap-2">
                    <span className="text-2xl">✕</span> Before
                  </h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="text-destructive mt-1">•</span>
                      Fragmented documents across drives and departments
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-destructive mt-1">•</span>
                      Siloed knowledge with no cross-diocese learning
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-destructive mt-1">•</span>
                      Duplicated effort on every new initiative
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-destructive mt-1">•</span>
                      No way to surface patterns or best practices
                    </li>
                  </ul>
                </CardContent>
              </Card>
              {/* After */}
              <Card className="border-primary/30 bg-primary/5">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
                    <span className="text-2xl">✓</span> After
                  </h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      Centralized repository with standardized taxonomy
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      AI-assisted search across all diocesan content
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      Shared learnings from successful programs
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      Pattern detection and insights for leadership
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-16 lg:py-24 bg-card/50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              How It Works
            </h2>
            <p className="text-lg text-muted-foreground">
              A phased approach that meets dioceses where they are.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {phases.map((phase, index) => (
              <Card key={index} className="border-border/50 bg-card text-center">
                <CardContent className="p-6 space-y-4">
                  <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                    <phase.icon className="h-8 w-8 text-primary" />
                  </div>
                  <div className="space-y-1">
                    <span className="text-sm font-medium text-primary uppercase tracking-wide">
                      {phase.phase}
                    </span>
                    <h3 className="text-xl font-semibold text-foreground">
                      {phase.title}
                    </h3>
                  </div>
                  <p className="text-muted-foreground">
                    {phase.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Who It's For
            </h2>
            <p className="text-lg text-muted-foreground">
              Built for the people doing the work of evangelization in Catholic dioceses.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {personas.map((persona, index) => (
              <Card key={index} className="border-border/50 bg-card text-center">
                <CardContent className="p-6 space-y-4">
                  <div className="mx-auto w-14 h-14 rounded-full bg-secondary/10 flex items-center justify-center">
                    <persona.icon className="h-7 w-7 text-secondary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">
                    {persona.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {persona.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pilot CTA Section */}
      <section className="py-16 lg:py-24 bg-primary/5 border-y border-primary/10">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground">
              Join the Pilot Program
            </h2>
            <p className="text-xl text-muted-foreground">
              We're inviting 3-5 dioceses to shape the future of diocesan evangelization.
            </p>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Pilot participants will help us build the right tool for the Church's needs. 
              You'll receive priority support, early access to new features, and the opportunity 
              to influence the platform's development.
            </p>
            <Button 
              size="lg" 
              onClick={() => setContactOpen(true)}
              className="text-lg px-8 mt-4"
            >
              Apply for the Pilot
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Common Questions
            </h2>
          </div>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <Card 
                key={index} 
                className="border-border/50 bg-card cursor-pointer"
                onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-foreground pr-4">
                      {faq.question}
                    </h3>
                    {expandedFaq === index ? (
                      <ChevronUp className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    )}
                  </div>
                  {expandedFaq === index && (
                    <p className="text-muted-foreground mt-4 pt-4 border-t border-border/50">
                      {faq.answer}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Dialog */}
      <ContactFormDialog
        open={contactOpen}
        onOpenChange={setContactOpen}
        defaultReason="Pilot Application"
        contactReasons={contactReasons}
      />
    </div>
  );
};

export default CrosierHome;
